import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { useAuth } from '../contexts/AuthContext';
import { useBranding, brandLogoSrc } from '../contexts/BrandingContext';
import {
  LayoutDashboard,
  Building2,
  Settings,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Smartphone,
  Package,
  CreditCard,
  Wallet,
  FileBarChart,
  Shield,
  Server,
  Globe,
  KeyRound,
  Clock,
  Utensils,
  Mail,
  Briefcase,
  Zap,
  Radio,
  BookOpen,
  Ticket,
  User,
  Sun,
  Moon,
  Monitor,
  Crown,
  UserPlus,
} from 'lucide-react';
import { cn } from '../lib/utils';
import api from '../lib/api';
import useTicketRealtime, { TICKET_ACTIVITY_EVENT } from '../hooks/useTicketRealtime';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/superadmin' },
  { icon: Building2, label: 'Dietitians', path: '/superadmin/companies' },
  { icon: UserPlus, label: 'Signup requests', path: '/superadmin/dietitian-requests' },
  { icon: Package, label: 'Add-ons', path: '/superadmin/addons' },
  { icon: Ticket, label: 'Tickets', path: '/superadmin/tickets' },
  {
    icon: User,
    label: 'Virtual Assistants',
    path: '/superadmin/virtual-assistants',
    children: [
      { label: 'Personal Assistant', path: '/superadmin/virtual-assistants/personal-assistant' },
      { label: 'Personal Dietitian', path: '/superadmin/virtual-assistants/personal-dietitian' },
    ],
  },
  { icon: BookOpen, label: 'Recipes', path: '/superadmin/recipes' },
  { icon: CreditCard, label: 'Payment Gateways', path: '/superadmin/payment-methods' },
  { icon: Crown, label: 'Customer Subscriptions', path: '/superadmin/mobile-premium' },
  { icon: Wallet, label: 'Dietitian Payments', path: '/superadmin/dietitian-payments' },
  { icon: FileBarChart, label: 'Reports', path: '/superadmin/reports' },
  { icon: Smartphone, label: 'App Installations', path: '/superadmin/app-installations' },
  { icon: Radio, label: 'Media Servers', path: '/superadmin/media-servers' },
];

const platformNavItems = [
  { icon: Shield, label: 'CAPTCHA', path: '/superadmin/platform/captcha' },
  { icon: Server, label: 'FTP Backup', path: '/superadmin/platform/ftp' },
  { icon: Globe, label: 'URLs', path: '/superadmin/platform/urls' },
  { icon: KeyRound, label: 'Login Auth', path: '/superadmin/platform/login-auth' },
  { icon: Clock, label: 'Schedulers', path: '/superadmin/platform/schedulers' },
  { icon: Utensils, label: 'Meal times', path: '/superadmin/settings?tab=mealtimes' },
  { icon: Mail, label: 'Mailer (mail server)', path: '/superadmin/settings?tab=mailer' },
  { icon: Briefcase, label: 'Rules', path: '/superadmin/platform/roles' },
  { icon: Zap, label: 'Events', path: '/superadmin/platform/events' },
];

const initialsFrom = (name, email) => {
  const source = String(name || email || 'S').trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
};

export const SuperAdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { branding } = useBranding();
  const { theme, setTheme } = useTheme();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [openTicketCount, setOpenTicketCount] = useState(0);

  // Company detail and tickets use their own inner scroll panes
  const isCompanyDetail =
    /^\/superadmin\/companies\/[^/]+/.test(location.pathname) &&
    !location.pathname.includes('/users');
  const lockMainScroll =
    isCompanyDetail || location.pathname.startsWith('/superadmin/tickets');

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname, location.search]);

  const saToken =
    typeof window !== 'undefined' ? localStorage.getItem('token') : '';

  const fetchTicketCounts = useCallback(async () => {
    try {
      const res = await api.get('/superadmin/tickets/count');
      const support = Number(res.data?.count) || 0;
      const sales = Number(res.data?.sales_count) || 0;
      const internal = Number(res.data?.internal_count) || 0;
      setOpenTicketCount(support + sales + internal);
    } catch {
      setOpenTicketCount(0);
    }
  }, []);

  useTicketRealtime({
    token: saToken || '',
    enabled: !!saToken,
    onTicket: fetchTicketCounts,
  });

  useEffect(() => {
    fetchTicketCounts();
    const interval = setInterval(fetchTicketCounts, 60000);
    const onActivity = () => fetchTicketCounts();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchTicketCounts();
    };
    window.addEventListener(TICKET_ACTIVITY_EVENT, onActivity);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      window.removeEventListener(TICKET_ACTIVITY_EVENT, onActivity);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchTicketCounts]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  const handleLogout = () => {
    logout();
    navigate('/superadmin/login');
  };

  const closeMobileNav = () => setMobileNavOpen(false);

  const logoSrc = brandLogoSrc(branding);
  const brandName = branding?.brand_name || 'DietCoach';
  const displayName = user?.name || user?.email || 'Super Admin';
  const initials = initialsFrom(user?.name, user?.email);
  const iconBtnClass =
    'relative p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors';
  const searchTab = new URLSearchParams(location.search).get('tab');
  const isMailerSettings =
    location.pathname === '/superadmin/settings' && searchTab === 'mailer';
  const isMealTimesSettings =
    location.pathname === '/superadmin/settings' && searchTab === 'mealtimes';
  const isSettingsActive =
    location.pathname === '/superadmin/settings' &&
    !isMailerSettings &&
    !isMealTimesSettings;

  const sidebar = (
    <>
      <div className="h-16 flex items-center justify-between px-4 border-b border-border gap-2 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={`${logoSrc}${logoSrc.includes('?') ? '&' : '?'}v=${encodeURIComponent(String(branding.rev || 0))}`}
            alt={brandName}
            className="h-8 w-8 rounded object-contain"
          />
          <div className="min-w-0">
            <div className="text-sm font-bold text-foreground leading-tight truncate">
              {brandName}
            </div>
            <div className="text-xs text-muted-foreground leading-tight truncate">
              Super Admin
            </div>
          </div>
        </div>
        <button
          type="button"
          className="md:hidden p-2 rounded-lg hover:bg-muted text-muted-foreground"
          onClick={closeMobileNav}
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 py-4 px-4 sidebar overflow-y-auto min-h-0 overscroll-contain">
        {navItems.map((item) => {
          if (item.children) {
            const parentActive =
              location.pathname === item.path ||
              location.pathname.startsWith(`${item.path}/`);
            return (
              <div key={item.path} className="mb-1.5">
                <div
                  className={cn(
                    'nav-item flex items-center gap-3.5 px-3.5 py-2.5 rounded-md',
                    parentActive
                      ? 'text-foreground'
                      : 'text-muted-foreground',
                  )}
                >
                  <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="text-sm font-medium flex-1">{item.label}</span>
                  <ChevronDown className="w-4 h-4" />
                </div>
                <div className="ml-5 border-l border-border pl-2">
                  {item.children.map((child) => {
                    const childActive = location.pathname === child.path;
                    return (
                      <Link
                        key={child.path}
                        to={child.path}
                        onClick={closeMobileNav}
                        data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={cn(
                          'block px-3 py-2 rounded-md text-sm mb-0.5 transition-colors',
                          childActive
                            ? 'bg-muted text-foreground'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        {child.label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          }
          const isActive =
            location.pathname === item.path ||
            (item.path !== '/superadmin' &&
              item.path !== '/superadmin/tickets' &&
              location.pathname.startsWith(`${item.path}/`));
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={closeMobileNav}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={cn(
                'nav-item flex items-center gap-3.5 px-3.5 py-2.5 rounded-md mb-1.5 transition-colors',
                isActive
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-sm font-medium flex-1">{item.label}</span>
              {item.path === '/superadmin/tickets' && openTicketCount > 0 && (
                <span className="text-[10px] font-semibold tabular-nums min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                  {openTicketCount > 99 ? '99+' : openTicketCount}
                </span>
              )}
            </Link>
          );
        })}
        <p className="px-3 pt-4 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Platform
        </p>
        {platformNavItems.map((item) => {
          const isActive = item.path.includes('tab=mailer')
            ? isMailerSettings
            : item.path.includes('tab=mealtimes')
              ? isMealTimesSettings
              : location.pathname === item.path ||
                location.pathname.startsWith(`${item.path}/`);
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={closeMobileNav}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={cn(
                'nav-item flex items-center gap-3.5 px-3.5 py-2.5 rounded-md mb-1.5 transition-colors',
                isActive
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-sm font-medium">{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </>
  );

  return (
    <div className="h-screen bg-background flex overflow-hidden">
      {mobileNavOpen && (
        <button
          type="button"
          className="md:hidden fixed inset-0 z-40 bg-black/50"
          aria-label="Close menu overlay"
          onClick={closeMobileNav}
        />
      )}

      <aside
        className={cn(
          'bg-card border-r border-border flex flex-col w-64 shrink-0',
          'fixed md:static inset-y-0 left-0 z-50 h-screen',
          'transition-transform duration-300 ease-in-out',
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        )}
        data-testid="superadmin-sidebar"
      >
        {sidebar}
      </aside>

      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        <header className="h-14 shrink-0 border-b border-border bg-card flex items-center justify-between gap-3 px-3 sm:px-4">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              className="md:hidden p-2 rounded-lg hover:bg-muted text-foreground shrink-0"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="min-w-0 hidden sm:block">
              <p className="text-sm font-semibold text-foreground truncate">
                {brandName}
              </p>
              <p className="text-[11px] text-muted-foreground truncate">
                Super Admin
              </p>
            </div>
          </div>

          <div className="flex items-center gap-0.5 sm:gap-1">
            <Link
              to="/superadmin/tickets"
              className={iconBtnClass}
              title="Tickets"
              aria-label="Tickets"
            >
              <Ticket className="w-5 h-5" />
              {openTicketCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
                  {openTicketCount > 99 ? '99+' : openTicketCount}
                </span>
              )}
            </Link>

            <Link
              to="/superadmin/settings?tab=mailer"
              className={iconBtnClass}
              title="Mailer"
              aria-label="Mailer"
            >
              <Mail className="w-5 h-5" />
            </Link>

            <Link
              to="/superadmin/settings"
              className={cn(
                iconBtnClass,
                isSettingsActive && 'text-foreground bg-muted'
              )}
              title="Settings"
              aria-label="Settings"
            >
              <Settings className="w-5 h-5" />
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="ml-1 flex items-center gap-2 rounded-full p-1 pr-1.5 sm:pr-2 hover:bg-muted transition-colors"
                  data-testid="superadmin-user-menu"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:flex flex-col items-start max-w-[140px]">
                    <span className="text-sm font-medium text-foreground truncate w-full text-left">
                      {displayName}
                    </span>
                    <span className="text-[11px] text-muted-foreground truncate w-full text-left">
                      {user?.email}
                    </span>
                  </span>
                  <ChevronDown className="hidden sm:block w-4 h-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{displayName}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {user?.email}
                      </p>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/superadmin/settings')}>
                  <User className="w-4 h-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/superadmin/settings')}>
                  <Settings className="w-4 h-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/superadmin/tickets')}>
                  <Ticket className="w-4 h-4" />
                  Tickets
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/superadmin/settings?tab=mailer')}>
                  <Mail className="w-4 h-4" />
                  Mailer
                </DropdownMenuItem>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Sun className="w-4 h-4" />
                    Theme
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem onClick={() => setTheme('light')}>
                      <Sun className="w-4 h-4" />
                      Light
                      {theme === 'light' ? ' ✓' : ''}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setTheme('dark')}>
                      <Moon className="w-4 h-4" />
                      Dark
                      {theme === 'dark' ? ' ✓' : ''}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setTheme('system')}>
                      <Monitor className="w-4 h-4" />
                      System
                      {theme === 'system' ? ' ✓' : ''}
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main
          className={cn(
            'flex-1 min-w-0 min-h-0',
            lockMainScroll ? 'overflow-hidden flex flex-col' : 'overflow-auto'
          )}
        >
          {children}
        </main>
      </div>
    </div>
  );
};
